import React, { useState } from 'react';
import { Link } from 'react-router-dom';

export default function FAQSection({ 
  title = "Ofte stillede spørgsmål", 
  subtitle = "Få svar på de mest almindelige spørgsmål herunder.", 
  badge = "FAQ & Rådgivning",
  items = [] 
}) {
  // Start with all closed so it doesn't take up too much vertical space initially
  const [openIndices, setOpenIndices] = useState([]);

  const toggleItem = (index) => {
    setOpenIndices((prev) => 
      prev.includes(index) ? prev.filter((i) => i !== index) : [...prev, index]
    );
  };

  const expandAll = () => {
    setOpenIndices(items.map((_, i) => i));
  };

  const collapseAll = () => {
    setOpenIndices([]);
  };

  if (!items || items.length === 0) return null;

  return (
    <div className="flex flex-col gap-3.5 pt-2 border-t border-slate-200/80">
      {/* Section Header - Compact */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 pt-2 pb-1">
        <div>
          <div className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-wider text-brand-accent mb-0.5">
            <i className="fa-solid fa-circle-question text-xs" />
            <span>{badge}</span>
          </div>
          <h3 className="text-lg md:text-xl font-bold text-brand-dark font-display">
            {title}
          </h3>
          {subtitle && (
            <p className="text-slate-500 text-xs md:text-sm mt-0.5 max-w-2xl">
              {subtitle}
            </p>
          )}
        </div>

        {/* Expand / Collapse Controls */}
        <div className="flex items-center gap-1.5 self-start sm:self-center shrink-0">
          <button
            type="button"
            onClick={expandAll}
            className="text-[11px] font-semibold text-slate-500 hover:text-brand-accent transition-colors px-2 py-1 rounded hover:bg-slate-100 cursor-pointer"
          >
            Åbn alle
          </button>
          <span className="text-slate-300 text-[10px]">•</span>
          <button
            type="button"
            onClick={collapseAll}
            className="text-[11px] font-semibold text-slate-500 hover:text-brand-accent transition-colors px-2 py-1 rounded hover:bg-slate-100 cursor-pointer"
          >
            Luk alle
          </button>
        </div>
      </div>

      {/* Accordion Items List - Compact & Space-efficient */}
      <div className="flex flex-col gap-2">
        {items.map((item, idx) => {
          const isOpen = openIndices.includes(idx);
          return (
            <div
              key={idx}
              className={`rounded-xl border transition-all duration-150 overflow-hidden ${
                isOpen 
                  ? 'bg-white border-brand-accent/30 shadow-xs ring-1 ring-brand-accent/10' 
                  : 'bg-slate-50/60 border-slate-200/70 hover:border-slate-300 hover:bg-white'
              }`}
            >
              <button
                type="button"
                onClick={() => toggleItem(idx)}
                aria-expanded={isOpen}
                className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left transition-colors cursor-pointer"
              >
                <div className="flex items-center gap-2.5 min-w-0 pr-2">
                  <span className={`flex h-5 w-5 shrink-0 items-center justify-center rounded text-[11px] font-bold transition-colors ${
                    isOpen 
                      ? 'bg-brand-accent text-white' 
                      : 'bg-slate-200/90 text-slate-600'
                  }`}>
                    {idx + 1}
                  </span>
                  <span className="text-sm md:text-base font-semibold text-brand-dark font-display leading-snug">
                    {item.question}
                  </span>
                </div>
                
                <span className={`flex h-6 w-6 shrink-0 items-center justify-center rounded-full transition-transform duration-200 ${
                  isOpen 
                    ? 'bg-brand-accent/10 text-brand-accent rotate-180' 
                    : 'bg-slate-100 text-slate-400'
                }`}>
                  <i className="fa-solid fa-chevron-down text-[10px]" />
                </span>
              </button>

              {isOpen && (
                <div className="px-4 pb-3.5 pt-0 border-t border-slate-100">
                  <div className="pt-2.5 text-slate-600 text-xs md:text-sm leading-relaxed pl-7 md:pl-7">
                    {typeof item.answer === 'string' ? (
                      <p>{item.answer}</p>
                    ) : (
                      item.answer
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Subtle discreet help text at the bottom */}
      <div className="flex items-center justify-between gap-3 px-3 py-2 rounded-lg bg-slate-50 border border-slate-200/50 text-xs text-slate-500 mt-1">
        <span>Har du et andet spørgsmål?</span>
        <div className="flex items-center gap-3 shrink-0">
          <Link
            to="/kontakt"
            className="text-brand-accent hover:underline font-semibold"
          >
            Skriv til os
          </Link>
          <span className="text-slate-300">•</span>
          <a
            href="tel:+4521684422"
            className="text-slate-700 hover:text-brand-accent font-semibold"
          >
            Tlf. 21 68 44 22
          </a>
        </div>
      </div>
    </div>
  );
}
